import { MoreHoriz as MoreIcon } from '@mui/icons-material';
import { Divider, IconButton, Menu, MenuItem } from '@mui/material';
import React, { useState } from 'react';
import type { TableCellStyle } from '../../template/Table';

interface TableLineMenuProps {
  orientation: 'row' | 'column';
  index: number;
  canDelete: boolean;
  onInsertBefore: () => void;
  onInsertAfter: () => void;
  onDelete: () => void;
  /** Row-only: set the style of every cell in this row. */
  onStyle?: (style: TableCellStyle) => void;
}

const ROW_STYLE_ITEMS: { style: TableCellStyle; label: string }[] = [
  { style: 'header', label: 'Header row' },
  { style: 'subHeader', label: 'Subheader row' },
  { style: 'normal', label: 'Normal row' },
];

/** The hover grip + menu in a row's left gutter or a column's top gutter. */
export default function TableLineMenu({
  orientation,
  index,
  canDelete,
  onInsertBefore,
  onInsertAfter,
  onDelete,
  onStyle,
}: TableLineMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const noun = orientation;
  const before = orientation === 'row' ? 'above' : 'left';
  const after = orientation === 'row' ? 'below' : 'right';

  const run = (action: () => void) => () => {
    action();
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={`${noun} ${index + 1} options`}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          opacity: 0,
          transition: 'opacity 0.12s',
          '.MuiTableRow-root:hover &, td:hover &': {},
        }}
        className="table-line-grip"
      >
        <MoreIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={run(onInsertBefore)}>
          Insert {noun} {before}
        </MenuItem>
        <MenuItem onClick={run(onInsertAfter)}>
          Insert {noun} {after}
        </MenuItem>
        <MenuItem onClick={run(onDelete)} disabled={!canDelete} sx={{ color: 'error.main' }}>
          Delete {noun}
        </MenuItem>
        {onStyle && <Divider />}
        {onStyle &&
          ROW_STYLE_ITEMS.map((item) => (
            <MenuItem key={item.style} onClick={run(() => onStyle(item.style))}>
              {item.label}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
}
